/**
 * Message the gate — a short note straight to the guard desk ("furniture
 * tempo coming at 5", "don't allow flyers today"). Wired: GET/POST
 * /api/services/guard-messages. Guard mode (Wave D) reads + marks seen.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, str, useGateList } from '@/components/gate/api';
import { InfoNote } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT, monthShort } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Input, Pill, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

type Msg = { id: string; body: string; seen: boolean; at: string };

function toMsg(m: unknown, i: number): Msg {
  const iso = str(m, 'createdAt') ?? '';
  const d = new Date(iso);
  let at = '';
  if (!Number.isNaN(d.getTime())) {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    at = `${d.getDate()} ${monthShort(d.getMonth())} · ${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  }
  return {
    id: str(m, 'id') ?? `m${i}`,
    body: str(m, 'body') ?? '',
    seen: bool(m, 'readByGuard') ?? false,
    at,
  };
}

export default function MessageGuardScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/services/guard-messages', 'messages');
  const rows = items.map(toMsg);
  const [draft, setDraft] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.post<unknown>('/api/services/guard-messages', { body: draft.trim() });
      setDraft('');
      await reload();
    } catch {
      setError(t('mg_send_error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('mg_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <InfoNote icon="mail" style={{ marginBottom: space.s4 }}>
          {t('mg_info')}
        </InfoNote>

        <Input
          value={draft}
          onChangeText={setDraft}
          placeholder={t('mg_placeholder')}
          multiline
          numberOfLines={3}
          maxLength={500}
          style={{ minHeight: 88, textAlignVertical: 'top' }}
        />
        {error ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s2 }}>{error}</AppText> : null}
        <Button
          title={t('mg_send')}
          size="lg"
          loading={sending}
          disabled={!draft.trim()}
          onPress={() => void send()}
          style={{ marginTop: space.s3 }}
        />

        <SectionLabel style={{ marginTop: space.s6 }}>{t('mg_sent')}</SectionLabel>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={64} />)}</View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="mail" size={40} color={colors.textTertiary} />}
            title={t('mg_empty_title')}
            body={t('mg_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((m) => (
              <Card key={m.id}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: space.s3 }}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <AppText variant="bodyMd">{m.body}</AppText>
                    {m.at ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>{m.at}</AppText> : null}
                  </View>
                  <Pill status={m.seen ? 'resolved' : 'pending'} label={m.seen ? 'SEEN' : 'SENT'} />
                </View>
              </Card>
            ))}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
